import { Alert, Card, Flex, Space, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  GPS_CAPTURE_STATES,
  type InventorizationPosition,
} from '@/features/inventorization/domain/inventorization-position';
import {
  DEFAULT_GPS_POINT,
  readGeoPoint,
  readMeterGeoPoint,
} from '@/features/inventorization/position/gps-position-geo';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import type { GeoPoint } from '@/routers/inventorization/components/position/GoogleMarkerMapPicker';
import { GpsPositionControls } from '@/routers/inventorization/components/position/GpsPositionControls';
import { GpsPositionMap } from '@/routers/inventorization/components/position/GpsPositionMap';
import { OtherMetersMapSection } from '@/routers/inventorization/components/position/OtherMetersMapSection';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

export function GpsPositionPicker({
  backLabel,
  description,
  excludeMeterId,
  headerExtra,
  onBack,
  onSave,
  position,
  saveLabel,
  section,
  title,
}: {
  backLabel: string;
  description: string;
  excludeMeterId?: string | null;
  headerExtra?: ReactNode;
  onBack: () => void;
  onSave: (patch: Partial<InventorizationPosition>) => void;
  position: InventorizationPosition;
  saveLabel: string;
  section: string;
  title: string;
}) {
  const { bootstrap } = useLocationBootstrap();
  const otherMeterPins = useMemo(
    () =>
      (bootstrap?.existingMeters ?? [])
        .filter(meter => meter._id !== excludeMeterId)
        .map(readMeterGeoPoint)
        .filter((point): point is GeoPoint => Boolean(point)),
    [bootstrap?.existingMeters, excludeMeterId],
  );
  const locationCenter = useMemo(() => readGeoPoint(bootstrap?.location), [bootstrap?.location]);
  const initialPin = useMemo(
    () =>
      position.gpsLat !== null && position.gpsLng !== null ? { lat: position.gpsLat, lng: position.gpsLng } : null,
    [position.gpsLat, position.gpsLng],
  );
  const [currentPosition, setCurrentPosition] = useState<GeoPoint | null>(null);
  const [focusPoint, setFocusPoint] = useState<GeoPoint | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [hasTouchedMap, setHasTouchedMap] = useState(false);
  const [selectedPin, setSelectedPin] = useState<GeoPoint | null>(initialPin);
  const [mapCenter, setMapCenter] = useState<GeoPoint>(() => initialPin ?? locationCenter ?? DEFAULT_GPS_POINT);
  const hasTouchedMapRef = useRef(hasTouchedMap);
  const selectedPinRef = useRef(selectedPin);

  useEffect(() => {
    setSelectedPin(initialPin);
    if (initialPin) {
      setMapCenter(initialPin);
      setFocusPoint(initialPin);
    }
  }, [initialPin]);

  useEffect(() => {
    hasTouchedMapRef.current = hasTouchedMap;
  }, [hasTouchedMap]);

  useEffect(() => {
    selectedPinRef.current = selectedPin;
  }, [selectedPin]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoError(
        translateInventoryText(
          'app.inventory.position.gps.geolocationUnavailable',
          'Geolocation is not available in this browser.',
        ),
      );

      return;
    }

    setIsLocating(true);
    const onSuccess: PositionCallback = result => {
      const nextPosition = {
        lat: result.coords.latitude,
        lng: result.coords.longitude,
      };

      setCurrentPosition({
        lat: nextPosition.lat,
        lng: nextPosition.lng,
      });
      if (!(selectedPinRef.current || hasTouchedMapRef.current)) {
        setMapCenter(nextPosition);
        setFocusPoint(nextPosition);
      }
      setGeoError(null);
      setIsLocating(false);
    };
    const onError: PositionErrorCallback = error => {
      setGeoError(
        error.message ||
          translateInventoryText('app.inventory.position.gps.readLocationError', 'Unable to read current location.'),
      );
      setIsLocating(false);
    };
    const options: PositionOptions = {
      enableHighAccuracy: true,
      maximumAge: 15_000,
      timeout: 20_000,
    };
    navigator.geolocation.getCurrentPosition(onSuccess, onError, options);
    const watchId = navigator.geolocation.watchPosition(onSuccess, onError, options);

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  function handleMapPick(point: GeoPoint) {
    setHasTouchedMap(true);
    setSelectedPin(point);
  }

  function useCurrentGps() {
    if (!currentPosition) {
      return;
    }

    setHasTouchedMap(true);
    setSelectedPin(currentPosition);
    setMapCenter(currentPosition);
    setFocusPoint({ ...currentPosition });
  }

  function saveCapturedPin() {
    if (!selectedPin) {
      return;
    }

    onSave({
      gpsLat: selectedPin.lat,
      gpsLng: selectedPin.lng,
      gpsState: GPS_CAPTURE_STATES.captured,
      gpsUnavailableReason: '',
    });
  }

  function skipGpsCapture() {
    onSave({
      gpsLat: null,
      gpsLng: null,
      gpsState: GPS_CAPTURE_STATES.unavailable,
      gpsUnavailableReason:
        geoError?.trim() ||
        translateInventoryText('app.inventory.position.gps.permissionUnavailable', 'GPS permission was not available.'),
    });
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Flex align="flex-start" gap="middle" justify="space-between" wrap>
          <div>
            <Typography.Text type="secondary">{section}</Typography.Text>
            <Typography.Title level={3} style={{ marginTop: 0 }}>
              {title}
            </Typography.Title>
            <Typography.Paragraph type="secondary">{description}</Typography.Paragraph>
          </div>
          {headerExtra}
        </Flex>

        {geoError ? <Alert closable message={geoError} showIcon type="warning" /> : null}

        <OtherMetersMapSection
          count={otherMeterPins.length}
          countDefaultMessage="{count} nearby pins"
          countMessageId="app.inventory.position.gps.nearbyPins"
          renderMap={showOtherMeters => (
            <GpsPositionMap
              currentPosition={currentPosition}
              focusPoint={focusPoint}
              mapCenter={mapCenter}
              onPick={handleMapPick}
              otherMeterPins={showOtherMeters ? otherMeterPins : []}
              selectedPin={selectedPin}
            />
          )}
        />

        <GpsPositionControls
          backLabel={backLabel}
          currentPosition={currentPosition}
          isLocating={isLocating}
          onBack={onBack}
          onSave={saveCapturedPin}
          onSelectPin={setSelectedPin}
          onSkipGps={skipGpsCapture}
          onUseCurrentGps={useCurrentGps}
          saveLabel={saveLabel}
          selectedPin={selectedPin}
          showSkipGps={Boolean(geoError && !currentPosition)}
        />
      </Space>
    </Card>
  );
}
